/**
PW Dropdown Menu

Wrap around any number of ReactBase.MenuItem components, or ...

!! Note about classNames on this component !!
  Any classNames set on the instance of this component will be merged with its own.


Options (props)
- activeMenuKeys [array]: Array of menuItem keys that should be considered "Active"
- label [string]: Label of the button that triggers the dropdown
- isRightAligned [bool]: Right-align the menu


example configuration:
  <ReactBase.DropdownMenu
    className="my_other_class"
    label="My Dropdown"
    isRightAligned={ true }
    activeMenuKeys={ ["foo", "biz"] }>

    <ReactBase.MenuItem key="foo">Foo</ReactBase.MenuItem>
    <ReactBase.MenuItem key="biz">Biz</ReactBase.MenuItem>
    <ReactBase.MenuItem key="bar">Bar</ReactBase.MenuItem>
    ...
  </ReactBase.DropdownMenu>

  Some code borrowed/inspired by https://github.com/react-bootstrap/react-bootstrap/blob/master/src/DropdownStateMixin.js
**/

// TODO add support for dividers and headings
// TODO improve keyboard nav, especially when filter field is focused

import _ from 'lodash'
import React, { cloneElement } from 'react'
import T from 'prop-types'
import MenuOwner from './MenuOwner'
import MenuItem from './MenuItem'
import ConfirmMenuItem from './ConfirmMenuItem'
import ScrollBars from './scrollbars/ScrollBars'
import FilterInput from './FilterInput'
import cx from 'classnames'
import { areComponentsEqual } from 'react-hot-loader'
import { stepFocus } from '../utils.js'

class DropdownMenu extends React.Component {
  static displayName = 'DropdownMenu'

  static propTypes = {
    activeMenuKeys: T.array,
    allowActiveItemClick: T.bool, // Allow onClick calls to `active` MenuItems
    buttonClasses: T.string,
    classes: T.string,
    className: T.string,
    closeOnItemClick: T.bool,
    disabled: T.bool,
    filterable: T.bool,
    isMenuOpen: T.bool,
    label: T.node,
    menuAnchor: T.string,
    omitCaret: T.bool,
    onFilterChange: T.func,
    onMenuStateChange: T.func,
    preventBtnClickPropagation: T.bool,
    scrollable: T.bool,
    stealth: T.bool,
    tooltip: T.string,
    children: T.node,
    menuClasses: T.string,
    isRightAligned: T.bool
  }

  static defaultProps = {
    allowActiveItemClick: false,
    label: 'No Selection',
    classes: '',
    menuClasses: '',
    buttonClasses: 'btn btn-default dropdown-toggle',
    omitCaret: false,
    preventBtnClickPropagation: false,
    disabled: false,
    closeOnItemClick: false,
    stealth: false,
    // NOTE if scrollable is set to true then the
    // class .dropdown-menu MUST be given an explicit height.
    scrollable: false,
    filterable: false,
    onFilterChange: null
  }

  constructor(props) {
    super(props)
    this.state = {
      isMenuOpen: false
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { isMenuOpen } = this.state
    if (prevState.isMenuOpen !== isMenuOpen) {
      const fn = this.props.onMenuStateChange
      if (_.isFunction(fn)) {
        fn(isMenuOpen)
      }
    }
  }

  // Instance methods
  closeMenu = () => {
    this.setMenuState(false)
  }

  openMenu = () => {
    this.setMenuState(true)
  }

  isActiveItem = childMenuItem => {
    if (childMenuItem.props.active) {
      return true
    }
    const activeKeys = this.props.activeMenuKeys
    if (activeKeys && activeKeys.length) {
      return _.some(activeKeys, key => {
        // coerce keys to strings since React does that internally
        return '' + key === '' + childMenuItem.key
      })
    }
    return childMenuItem.props.active
  }

  handleButtonClick = e => {
    if (this.props.preventBtnClickPropagation) {
      e.stopPropagation()
    }
    const prevState = this.state.isMenuOpen
    this.setMenuState(!prevState)
  }

  handleFilterChange = value => {
    if (typeof this.props.onFilterChange === 'function') {
      this.props.onFilterChange(value)
    }
  }

  setMenuState = toOpen => {
    if (!toOpen && this.props.filterable) {
      this.handleFilterChange('') // Reset filter when menu closed
    }
    // Return focus to trigger el
    if (!toOpen && this._btnEl) {
      this._btnEl.focus()
    }
    this.setState({
      isMenuOpen: toOpen
    })
  }

  _onBtnRef = el => {
    this._btnEl = el
  }

  _onMenuElRef = el => {
    this._menuEl = el
  }

  _onKeyDown = e => {
    if (!e.isDefaultPrevented()) {
      // Close menu on Escape key
      if (e.key === 'Escape' || e.key === 'Tab') {
        this.closeMenu()
      }
      // Arrow keys open the menu and/or step through focus within menu
      else if (/Up|Down/.test(e.key)) {
        if (this._menuEl) {
          stepFocus(this._menuEl,/Up/.test(e.key) ? -1 : 1)
        } else {
          this.openMenu()
        }
        e.preventDefault()
      }
    }
  }

  _processMenuItem = childMenuItem => {
    // TODO handle nested dropdowns/flyouts?
    return childMenuItem &&
    (areComponentsEqual(childMenuItem.type, MenuItem) || areComponentsEqual(childMenuItem.type, ConfirmMenuItem))
      ? cloneElement(childMenuItem, {
          active: this.isActiveItem(childMenuItem),
          key: childMenuItem.key,
          ref: childMenuItem.ref,
          onClick: (...clickArgs) => {
            if (this.props.closeOnItemClick) {
              this.closeMenu()
            }
            // Delegate to the child's onClick or href
            const childProps = childMenuItem.props
            if (childProps.onClick) {
              if (
                !childProps.disabled &&
                (this.props.allowActiveItemClick || !childProps.active)
              ) {
                childProps.onClick(...clickArgs)
              }
            } else if (childProps.href && !childProps.disabled) {
              location.href = childProps.href
            }
          },
          // default to passing the key:
          args:
            childMenuItem.props.args != null
              ? childMenuItem.props.args
              : childMenuItem.key
        })
      : childMenuItem
  }

  _renderMenu = () => {
    const {
      children,
      menuClasses,
      isRightAligned,
      scrollable,
      filterable
    } = this.props
    const menuItems = React.Children.map(children, this._processMenuItem)
    let menu = (
      <ul>
        {menuItems}
      </ul>
    )
    if (scrollable) {
      menu = (
        <ScrollBars classes="pw_scrollbars_light">
          {menu}
        </ScrollBars>
      )
    }

    return (
      <div
        className={cx(menuClasses, {
          'dropdown-menu': true,
          'dropdown-menu-right': isRightAligned,
          scrollable: scrollable,
          filterable: filterable
        })}
        ref={this._onMenuElRef}
        onKeyDown={this._onKeyDown}
      >
        {filterable
          ? <FilterInput
              className="form-control filter"
              onChange={this.handleFilterChange}
              placeholder="begin typing"
              focusOnMount
            />
          : null}
        <div className="items">
          {menu}
        </div>
      </div>
    )
  }

  render() {
    const {
      classes,
      className,
      disabled,
      menuAnchor,
      scrollable,
      filterable,
      stealth,
      buttonClasses,
      tooltip,
      label,
      omitCaret
    } = this.props
    const { isMenuOpen } = this.state
    const componentClasses = cx(classes, className, {
      pw_dropdown: true,
      disabled: disabled,
      'btn-group': true,
      stealth_dropdown: stealth,
      open: isMenuOpen,
      dropup: menuAnchor && menuAnchor.indexOf('top') === 0,
      scrollable: scrollable,
      filterable: filterable
    })

    return (
      <MenuOwner
        className={componentClasses}
        onClose={this.closeMenu}
        renderMenu={this._renderMenu}
        isMenuOpen={isMenuOpen}
        menuAnchor={menuAnchor}
      >
        <button
          className={buttonClasses}
          data-tooltip={tooltip}
          disabled={disabled}
          onClick={this.handleButtonClick}
          onKeyDown={this._onKeyDown}
          type="button"
          ref={this._onBtnRef}
        >
          {label}
          {omitCaret || disabled ? '' : <span className="caret" />}
        </button>
      </MenuOwner>
    )
  }
}

export default DropdownMenu
